<template>
  <div class="card mt-4">
    <div class="card-header pb-0" style="cursor:pointer;" @click="toggleExpanded">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h6 class="mb-0">Audit Events</h6>
          <p class="text-sm text-secondary mb-0">Recent scheduling, update, and delete events</p>
        </div>
        <div class="d-flex align-items-center gap-2">
          <span class="text-xs text-secondary" style="cursor:help;" @click.stop
            title="Displays recent update, scheduled, and delete events from Activity Instance Workgroup audit logs. Click the details button on any row to view the full audit log.">
            <i class="material-icons text-sm align-middle">help_outline</i>
          </span>
          <i class="material-icons text-secondary align-middle">{{ expanded ? 'expand_less' : 'expand_more' }}</i>
        </div>
      </div>

      <!-- Filters row -->
      <div v-if="expanded" class="d-flex flex-wrap align-items-center gap-2 mt-2 mb-1" @click.stop>
        <select class="form-select form-select-sm" style="width:auto;min-width:90px;" v-model="filters.type" @change="applyFilters">
          <option value="">All Types</option>
          <option value="update">Update</option>
          <option value="scheduled">Scheduled</option>
          <option value="delete">Delete</option>
        </select>
        <select class="form-select form-select-sm" style="width:auto;min-width:120px;" v-model="filters.activity" @change="applyFilters">
          <option value="">All Activities</option>
          <option v-for="a in filterOptions.activities" :key="a" :value="a">{{ a }}</option>
        </select>
        <select class="form-select form-select-sm" style="width:auto;min-width:120px;" v-model="filters.workgroup" @change="applyFilters">
          <option value="">All Workgroups</option>
          <option v-for="w in filterOptions.workgroups" :key="w" :value="w">{{ w }}</option>
        </select>
        <select v-if="scope !== 'location'" class="form-select form-select-sm" style="width:auto;min-width:120px;" v-model="filters.location" @change="applyFilters">
          <option value="">All Locations</option>
          <option v-for="l in filterOptions.locations" :key="l" :value="l">{{ l }}</option>
        </select>
        <select v-if="scope === 'all'" class="form-select form-select-sm" style="width:auto;min-width:120px;" v-model="filters.organization" @change="applyFilters">
          <option value="">All Orgs</option>
          <option v-for="o in filterOptions.organizations" :key="o" :value="o">{{ o }}</option>
        </select>
        <select class="form-select form-select-sm" style="width:auto;min-width:100px;" v-model="rangeMonths" @change="applyFilters">
          <option :value="1">Last 1 month</option>
          <option :value="2">Last 2 months</option>
          <option :value="3">Last 3 months</option>
          <option :value="6">Last 6 months</option>
          <option :value="12">Last 12 months</option>
        </select>
      </div>
    </div>

    <div v-if="expanded" class="card-body px-0 pt-0 pb-2">
      <BaseTable
        :rows="rows"
        :columns="computedColumns"
        idKey="audit_id"
        searchable
        :selectable="false"
        :loading="loading"
        :pagination="pagination"
        @changePage="onPageChange"
        @updatePageSize="onPageSizeChange"
        @searchChanged="onSearchChanged"
      >
        <template #col-message_type="{ row }">
          <span class="badge" :class="{
            'bg-gradient-info': row.message_type === 'update',
            'bg-gradient-success': row.message_type === 'scheduled',
            'bg-gradient-danger': row.message_type === 'delete',
          }" style="font-size:11px;">{{ row.message_type }}</span>
        </template>
        <template #col-message="{ row }">
          <span class="text-truncate d-inline-block" style="max-width: 280px;" :title="row.message">
            {{ truncate(row.message, 55) }}
          </span>
        </template>
        <template #actions="{ row }">
          <button class="btn btn-sm btn-outline-info mb-0 py-1 px-2" @click="viewDetails(row)" title="View full audit log">
            <i class="material-icons text-sm">visibility</i>
          </button>
        </template>
      </BaseTable>
    </div>

    <!-- Detail popup modal -->
    <ContentModal v-model="detailModalOpen" :title="detailModalHeading" size="xl">
      <div v-html="detailModalContent"></div>
    </ContentModal>
  </div>
</template>

<script setup>
import { ref, computed, reactive } from 'vue'
import BaseTable from '@/components/Table/Table.vue'
import ContentModal from '@/components/Modal/ContentModal.vue'
import api from '@/config/api'
import { getLocationId } from '@/utils/SessionDataHandler'

const props = defineProps({
  scope: { type: String, default: 'location' },
  showOrganization: { type: Boolean, default: false },
})

const expanded = ref(false)
const hasLoadedOnce = ref(false)
const loading = ref(true)
const rows = ref([])
const pagination = ref({
  currentPage: 1,
  pageSize: 10,
  totalRecords: 0,
  totalPages: 0,
  hasPrevious: false,
  hasNext: false,
})
const searchTerm = ref('')
const rangeMonths = ref(1)
const detailModalOpen = ref(false)
const detailModalHeading = ref('')
const detailModalContent = ref('')

const filters = reactive({
  type: '',
  activity: '',
  workgroup: '',
  location: '',
  organization: '',
})

const filterOptions = reactive({
  activities: [],
  workgroups: [],
  locations: [],
  organizations: [],
})

const baseColumns = [
  { key: 'log_date', label: 'LOG DATE' },
  { key: 'activity', label: 'ACTIVITY' },
  { key: 'workgroup', label: 'WORKGROUP' },
  { key: 'instance_date', label: 'INSTANCE DATE' },
  { key: 'message_type', label: 'TYPE' },
  { key: 'message', label: 'MESSAGE' },
  { key: 'location', label: 'LOCATION' },
]

const computedColumns = computed(() => {
  const cols = [...baseColumns]
  if (props.showOrganization) {
    cols.push({ key: 'organization', label: 'ORGANIZATION' })
  }
  return cols
})

function truncate(str, len) {
  if (!str) return ''
  return str.length > len ? str.substring(0, len) + '...' : str
}

async function fetchEvents(page = 1, pageSize = 10) {
  loading.value = true
  const locationId = getLocationId()
  if (!locationId) { loading.value = false; return }

  try {
    const response = await api.post('/managelocationajax', {
      cmd: 'entityauditevents',
      locationid: locationId,
      page_number: page,
      page_size: pageSize,
      scope: props.scope,
      search: searchTerm.value,
      range_months: rangeMonths.value,
      filter_type: filters.type,
      filter_activity: filters.activity,
      filter_workgroup: filters.workgroup,
      filter_location: filters.location,
      filter_organization: filters.organization,
    })
    rows.value = response.data?.output || []
    // Map snake_case backend pagination to camelCase expected by BaseTable/PaginationFooter
    const pag = response.data?.pagination || {}
    pagination.value = {
      currentPage: pag.current_page || 1,
      pageSize: pag.page_size || pageSize,
      totalRecords: pag.total_records || 0,
      totalPages: pag.total_pages || 0,
      hasPrevious: pag.has_previous || false,
      hasNext: pag.has_next || false,
    }

    // Build filter options from first page of data
    if (page === 1 && rows.value.length > 0) {
      const acts = new Set(), wgs = new Set(), locs = new Set(), orgs = new Set()
      rows.value.forEach(r => {
        if (r.activity) acts.add(r.activity)
        if (r.workgroup) wgs.add(r.workgroup)
        if (r.location) locs.add(r.location)
        if (r.organization) orgs.add(r.organization)
      })
      filterOptions.activities = [...acts].sort()
      filterOptions.workgroups = [...wgs].sort()
      filterOptions.locations = [...locs].sort()
      filterOptions.organizations = [...orgs].sort()
    }
  } catch (e) {
    console.error('Error fetching audit events:', e)
    rows.value = []
  } finally {
    loading.value = false
  }
}

async function viewDetails(row) {
  detailModalHeading.value = `Audit Log: ${row.workgroup || 'Workgroup'} - ${row.instance_date || ''}`
  detailModalContent.value = '<p>Loading audit log details...</p>'
  detailModalOpen.value = true

  try {
    const locationId = getLocationId()
    const response = await api.post('/managelocationajax', {
      cmd: 'getAIWGauditlogs',
      locationid: locationId,
      aworkgroupinstanceid: row.aiwg_id
    })
    const logs = response.data || []
    if (logs.length === 0) {
      detailModalContent.value = '<p>No audit log entries found.</p>'
      return
    }
    let html = '<div style="max-height:500px;overflow-y:auto;"><table class="table table-sm table-striped" style="font-size:13px;"><thead><tr><th>Date</th><th>Type</th><th style="width:70%;">Message</th></tr></thead><tbody>'
    for (const log of logs) {
      const typeColor = log.messagetype === 'update' ? '#2563eb' : log.messagetype === 'delete' ? '#dc2626' : log.messagetype === 'scheduled' ? '#16a34a' : '#6b7280'
      html += `<tr><td style="white-space:nowrap;">${log.messagedt}</td><td><span style="color:${typeColor};font-weight:600;">${log.messagetype}</span></td><td style="word-break:break-word;">${log.message}</td></tr>`
    }
    html += '</tbody></table></div>'
    detailModalContent.value = html
  } catch (e) {
    detailModalContent.value = '<p class="text-danger">Failed to load audit log details.</p>'
    console.error('Error fetching AIWG audit logs:', e)
  }
}

function applyFilters() { fetchEvents(1, pagination.value?.pageSize || 10) }
function onPageChange(page) { fetchEvents(page, pagination.value?.pageSize || 10) }
function onPageSizeChange(size) { fetchEvents(1, size) }
function onSearchChanged(term) { searchTerm.value = term || ''; fetchEvents(1, pagination.value?.pageSize || 10) }

// Collapsed by default to save space on the Admin Tools page; loads the form
// and table lazily the first time the card is expanded.
function toggleExpanded() {
  expanded.value = !expanded.value
  if (expanded.value && !hasLoadedOnce.value) {
    hasLoadedOnce.value = true
    fetchEvents()
  }
}
</script>
